import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Machines", id: "machines" },
  { label: "How it works", id: "how-it-works" },
  { label: "Contact", id: "quote" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <motion.header
        data-testid="nav-container"
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-[#FBF7EF]/85 backdrop-blur-xl border-b border-black/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo onClick={() => setOpen(false)} />
          <nav className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-sm font-medium text-stone-600 transition-colors hover:text-[#1B365D]"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-quote-cta-button"
              onClick={() => go("quote")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#1B365D] px-5 py-2.5 text-sm font-semibold text-[#FBF7EF] transition-all hover:-translate-y-0.5 hover:bg-[#2A4A73] hover:shadow-[0_10px_30px_rgba(27,54,93,0.35)]"
            >
              Get a quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </nav>
          <button
            data-testid="nav-mobile-toggle"
            className="rounded-full border border-black/15 p-2.5 text-[#16120E] md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-x-0 top-0 z-40 bg-[#FBF7EF]/97 pt-24 pb-10 backdrop-blur-2xl md:hidden"
          >
            <nav className="flex flex-col gap-2 px-6">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.id)}
                  className="rounded-2xl px-4 py-4 text-left font-display text-3xl font-extrabold tracking-tight text-[#16120E] transition-colors hover:text-[#1B365D]"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => go("quote")}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#1B365D] px-6 py-4 font-semibold text-[#FBF7EF]"
              >
                Get a free quote <ArrowRight className="h-4 w-4" />
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
